import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  ButtonGroup,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  InputAdornment,
  Switch,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SearchIcon from '@mui/icons-material/Search';
import { borderRadius, layoutGap } from '../../../theme/theme';
import {
  formatExportRunLog,
  type ExportLogStatus,
  type ExportRunLog,
} from '../lib/exportRunLog';
import ExportRunLogEntries from './ExportRunLogEntries';

type ExportLogFilter = 'all' | ExportLogStatus;
type CopyState = 'idle' | 'copied' | 'failed';

interface ExportRunLogWindowProps {
  readonly open: boolean;
  readonly log: ExportRunLog | null;
  readonly onClose: () => void;
}

export default function ExportRunLogWindow({
  open,
  log,
  onClose,
}: ExportRunLogWindowProps) {
  const theme = useTheme();
  const [filter, setFilter] = useState<ExportLogFilter>('all');
  const [query, setQuery] = useState('');
  const [technicalDetails, setTechnicalDetails] = useState(false);
  const [copyState, setCopyState] = useState<CopyState>('idle');

  useEffect(() => {
    setFilter('all');
    setQuery('');
    setTechnicalDetails(false);
    setCopyState('idle');
  }, [log]);

  const visibleEntries = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return (log?.entries ?? []).filter((entry) => {
      const statusMatches = filter === 'all' || entry.status === filter;
      const searchableText = [
        entry.subject,
        entry.message,
        entry.stage,
        entry.exceptionType,
        ...(entry.elementIds ?? []),
        entry.suggestedAction,
      ].filter(Boolean).join(' ').toLowerCase();
      const queryMatches = normalizedQuery.length === 0
        || searchableText.includes(normalizedQuery);
      return statusMatches && queryMatches;
    });
  }, [filter, log?.entries, query]);

  const handleCopy = async () => {
    if (!log || !navigator.clipboard) {
      setCopyState('failed');
      return;
    }
    try {
      await navigator.clipboard.writeText(formatExportRunLog(log));
      setCopyState('copied');
    } catch {
      setCopyState('failed');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      aria-labelledby="export-run-log-title"
      aria-describedby="export-run-log-description"
      data-testid="export-run-log-window"
      PaperProps={{
        sx: {
          width: { xs: 'calc(100% - 24px)', sm: '100%' },
          m: { xs: 1.5, sm: 2 },
          maxHeight: { xs: 'calc(100% - 24px)', sm: 'calc(100% - 32px)' },
          borderRadius: `${borderRadius.lg}px`,
          backgroundColor: theme.palette.background.paper,
          backgroundImage: 'none',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle id="export-run-log-title" sx={{ pb: 1, fontWeight: 700 }}>
        PCF export log
      </DialogTitle>

      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: layoutGap.section }}>
        <Typography
          id="export-run-log-description"
          variant="body2"
          sx={{ color: theme.palette.text.secondary }}
        >
          Review the latest run, including files written, warnings, and failed piping lines.
        </Typography>
        {log ? (
          <>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: layoutGap.dense }}>
              <Chip size="small" color="success" label={`${log.successful} succeeded`} />
              <Chip
                size="small"
                color="warning"
                label={`${log.warnings} warning${log.warnings === 1 ? '' : 's'}`}
              />
              <Chip size="small" color="error" label={`${log.failures} failed`} />
            </Box>

            <Box sx={{ display: 'flex', gap: layoutGap.dense, flexWrap: 'wrap', alignItems: 'center' }}>
              <ButtonGroup
                size="small"
                aria-label="Filter export log"
                sx={{
                  width: { xs: '100%', sm: 'auto' },
                  '& .MuiButtonGroup-grouped': {
                    flex: { xs: 1, sm: 'initial' },
                    minWidth: 0,
                    px: { xs: 0.5, sm: 1.25 },
                    fontSize: { xs: '0.7rem', sm: '0.8125rem' },
                  },
                }}
              >
                {([
                  ['all', 'All', log.entries.length],
                  ['success', 'Success', log.successful],
                  ['warning', 'Warnings', log.warnings],
                  ['failure', 'Failed', log.failures],
                ] as const).map(([value, label, count]) => (
                  <Button
                    key={value}
                    variant={filter === value ? 'contained' : 'outlined'}
                    onClick={() => setFilter(value)}
                    aria-pressed={filter === value}
                  >
                    {label} ({count})
                  </Button>
                ))}
              </ButtonGroup>
              <TextField
                size="small"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search filename, line, or reason"
                inputProps={{ 'aria-label': 'Search export log' }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: { xs: '100%', sm: 280 }, flex: '1 1 280px' }}
              />
            </Box>

            <ExportRunLogEntries entries={visibleEntries} />

            <FormControlLabel
              control={(
                <Switch
                  checked={technicalDetails}
                  onChange={(event) => setTechnicalDetails(event.target.checked)}
                />
              )}
              label="Show technical details"
            />
            {technicalDetails && (
              <Box
                component="dl"
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'max-content minmax(0, 1fr)' },
                  gap: 0.5,
                  m: 0,
                  p: 1.25,
                  borderRadius: `${borderRadius.md}px`,
                  backgroundColor: alpha(theme.palette.text.primary, 0.04),
                  '& dt': { fontWeight: 700 },
                  '& dd': { m: 0, overflowWrap: 'anywhere' },
                }}
              >
                <Typography component="dt" variant="caption">Completed</Typography>
                <Typography component="dd" variant="caption">{new Date(log.completedAt).toLocaleString()}</Typography>
                <Typography component="dt" variant="caption">Mode</Typography>
                <Typography component="dd" variant="caption">
                  {log.mode === 'model' ? 'Selected in Revit model' : 'Export table'}
                </Typography>
                <Typography component="dt" variant="caption">Output folder</Typography>
                <Typography component="dd" variant="caption">{log.outputPath || 'Not reported'}</Typography>
                <Typography component="dt" variant="caption">Trace ID</Typography>
                <Typography component="dd" variant="caption">{log.traceId ?? 'Not reported'}</Typography>
              </Box>
            )}
          </>
        ) : (
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
            Run a PCF export to create a log.
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1 }}>
        <Typography role="status" variant="caption" sx={{ color: theme.palette.text.secondary, mr: 'auto' }}>
          {copyState === 'copied'
            ? 'Log copied.'
            : copyState === 'failed'
              ? 'Could not copy the log.'
              : ''}
        </Typography>
        <Button
          onClick={() => void handleCopy()}
          startIcon={<ContentCopyIcon />}
          disabled={!log}
        >
          Copy report
        </Button>
        <Button variant="contained" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
